import React, { useState, useEffect, useRef, memo } from 'react';
import { Autocomplete, OverlayView } from '@react-google-maps/api';
import _ from 'lodash';
import axios from 'axios';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import Help from '@material-ui/icons/Help';
import WarningIcon from '@material-ui/icons/Warning';
import Tooltip from '@material-ui/core/Tooltip';
import Paper from '@material-ui/core/Paper';

const containerStyle = {
    width: '100%',
    height: '100%'
};

const center = {
    lat: 47.373,
    lng: 8.523
};

const markerColors = {
    0: 'green',
    1: 'red',
    2: 'blue',
    3: 'yellow',
    4: 'orange',
    5: 'purple',
    6: 'pink',
    7: 'gray'
};

const libraries = ["places"]
const markerSequenceHelp = "It is recommended that markers on the map with the same colors are close to each other in the following sequence."
const markerHasPlaceholder = "Attention! This marker has placeholders"

const Map = (props) => {
    const autocomplete = useRef(null)
    const [map, setMap] = useState(null)

    const onLoad = React.useCallback(function callback(map) {
        const bounds = new window.google.maps.LatLngBounds();
        map.fitBounds(bounds);
        setMap(map)
    }, [])

    const onUnmount = React.useCallback(function callback(map) {
        setMap(null)
    }, [])

    useEffect(() => {
        if (!props.center) setTimeout(() => { props.setCenter(center); }, 800)
    }, [map])

    const onPlaceChanged = async () => {
        if (autocomplete !== null) {
            const { geometry: { location }, name } = autocomplete.current.getPlace();
            const lat = location.lat();
            const lng = location.lng();

            const getImg = async () => {
                const { data } = await axios.get(`https://api.teleport.org/api/locations/${lat},${lng}/`);

                return data._embedded;
            }

            const getHref = async (href) => {
                const { data: { photos } } = await axios.get(href);

                return photos;
            }

            const img = await getImg();
            const urbanAreas = _.get(img, 'location:nearest-urban-areas');
            let photo;
            if (Array.isArray(urbanAreas)) {
                const href = _.get(urbanAreas[0], '_links.location:nearest-urban-area.href')

                if (href) {
                    const photos = await getHref(href + 'images');
                    if (Array.isArray(photos)) {
                        photo = photos[0].image.web;
                    }
                }
            }

            if (photo) {
                props.setBackgroundImage(photo);
            }

            const center = {
                lat: location.lat(),
                lng: location.lng()
            }

            props.setCenter(center);
        } else {
            console.log('Autocomplete is not loaded yet!')
        }
    }

    const getPixelPositionOffset = (offsetWidth, offsetHeight, labelAnchor) => ({
        x: offsetWidth + labelAnchor.x,
        y: offsetHeight + labelAnchor.y,
    });

    const getPlaceholders = (marker) => {
        const pattern = /{{(.*?)}}$/g
        const match = Object.keys(marker).map(key => {
            if (typeof marker[key] === 'string') {
                const match = marker[key].match(pattern);
                if (Array.isArray(match)) {
                    return true
                }

                return false;
            }

            return false;
        }).filter(Boolean).length;

        return match;
    }

    const renderMarker = (val, key) => (
        <div key={key}>
            <Marker
                draggable
                icon={{
                    url: `https://maps.google.com/mapfiles/ms/icons/${markerColors[val['flow_stage'] % 7] || 'red'}.png`
                }}
                onDragEnd={({ latLng }) => props.updateMarker(key, latLng.lat(), latLng.lng())}
                position={{
                    lat: val.coordinates.latitude,
                    lng: val.coordinates.longitude
                }}
                onClick={() => props.setActiveMarker(key)}
            />
            <OverlayView
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                position={{
                    lat: val.coordinates.latitude,
                    lng: val.coordinates.longitude
                }}
                getPixelPositionOffset={(x, y) => getPixelPositionOffset(x, y, { x: -80 - val['name'].length * 2, y: -90 })}
            >
                <div
                    className="capitalize"
                    style={{
                        background: markerColors[val['flow_stage'] % 7],
                        padding: `7px 12px`,
                        fontSize: '12px',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        opacity: .80,
                        color: (val['flow_stage'] % 7 === 2 || val['flow_stage'] % 7 === 5) ? 'white' : 'black',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minWidth: '100px',
                    }}
                >
                    {val['name'].replaceAll('_', ' ')}
                    {val.unlocks !== 'END' && <Tooltip style={{ marginLeft: '.5rem' }} title={val['context_en']}>
                        <Help fontSize="small" />
                    </Tooltip>
                    }
                </div>
            </OverlayView>
        </div>
    )

    return (
        <LoadScript
            googleMapsApiKey="AIzaSyAGGLldxVA_goaGG5uQ7RMHtqJ2IW_OwZk"
            libraries={libraries}
        >
            <GoogleMap
                mapContainerStyle={containerStyle}
                zoom={16}
                center={props.center}
                onLoad={onLoad}
                onUnmount={onUnmount}
                onDragEnd={() => props.setCenter({
                    lat: map.center.lat(),
                    lng: map.center.lng()
                })}
            >
                <Paper elevation={6} style={{
                    backgroundColor: '#ecf3ec',
                    position: 'absolute',
                    padding: '.5rem',
                    right: '5%',
                    top: '15%',
                    maxWidth: `${6}vw`,
                    overflow: 'hidden'

                }}>
                    <Tooltip title={markerSequenceHelp}>
                            <Help fontSize="small" />
                        </Tooltip>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
                        Marker Sequence
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        {Object.entries(markerColors).map(([key,value],index) => <div key={key}><div style={{ backgroundColor: value }} className='box'>{index + 1}</div></div>)}
                    </div>
                </Paper>
                <Autocomplete
                    onLoad={a => autocomplete.current = a}
                    onPlaceChanged={onPlaceChanged}
                >
                    <input
                        type="text"
                        placeholder="Where do you want to play?"
                        style={{
                            boxSizing: `border-box`,
                            border: `1px solid transparent`,
                            width: `40vw`,
                            height: `50px`,
                            padding: `0 12px`,
                            borderRadius: `3px`,
                            boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
                            fontSize: `14px`,
                            outline: `none`,
                            textOverflow: `ellipses`,
                            transform: "translate(-50%, -50%)",
                            position: "absolute",
                            left: "50%",
                            top: '10%',
                        }}
                    />
                </Autocomplete>
                {_.map(props.locations, (val, key) => renderMarker(val, key))}
                <></>
            </GoogleMap>
        </LoadScript>
    )
}

export default memo(Map)